import { type AxialCoord, hexDistance, hexEquals } from "../hex";
import { findHex, hexAt } from "../run/board";
import { seatOfUnit, soldierSeats } from "../run/seats";
import { allied } from "../run/sides";
import { payDeed, payDeeds } from "../soldier/bounty";
import { enlistedWith, isAtWork, marchesWith, onDuty } from "../soldier/company";
import { seatLeader } from "../soldier/lead";
import type { RunRules } from "../types/rules";
import type { FactionId, HexState, MutableHex, RunState } from "../types/state";
import { isWorkingCity } from "./cities";

/**
 * Who holds the land: the land a working city holds, taking a hex and the land that falls with a
 * city, and capture by standing.
 */

/**
 * The hex changes hands: a build still in progress is lost with it, a finished one changes hands;
 * a mayor goes with the old owner, and so do a city's kind and arrow (CC1): the new owner's
 * city starts at Auto and Home.
 */
function capture(hex: MutableHex, faction: FactionId): void {
  hex.owner = faction;
  hex.mayor = false;
  if (hex.structureReadyTick !== 0) {
    hex.structure = "none";
    hex.structureReadyTick = 0;
  }
  hex.unitBuild = "none";
  hex.unitBuildReadyTick = 0;
  delete hex.makes;
  delete hex.arrow;
  delete hex.arrowTick;
}

/**
 * A51, the land of cities: a faction's hex within `cityReach` of one of its working cities
 * is held by that city and cannot be taken by standing; it changes hands with the city. The
 * city hex itself is never held: it is taken by standing once its walls are down.
 */
export function heldByCityIn(
  hexes: readonly HexState[],
  rules: RunRules,
  tick: number,
  hex: HexState,
): boolean {
  if (!rules.cityHoldsLand || hex.owner === "none" || hex.razed || isWorkingCity(hex, tick)) {
    return false;
  }
  return hexes.some(
    (city) =>
      city.owner === hex.owner &&
      isWorkingCity(city, tick) &&
      hexDistance(city.coord, hex.coord) <= rules.cityReach,
  );
}

export function heldByCity(state: RunState, coord: AxialCoord): boolean {
  const hex = findHex(state, coord);
  return hex !== undefined && heldByCityIn(state.hexes, state.rules, state.tick, hex);
}

/** A hex a unit of another owner could take by standing on it: unrazed, wall-free, not held by a city. */
export function capturableByStanding(state: RunState, coord: AxialCoord): boolean {
  const hex = findHex(state, coord);
  return (
    hex !== undefined &&
    !hex.razed &&
    hex.walls === 0 &&
    !heldByCityIn(state.hexes, state.rules, state.tick, hex)
  );
}

/**
 * Whether `faction` takes `hex` by standing on it or by stepping in: no ally's (K6) nor its own,
 * unrazed, wall-free and held by no working city on `board` (the run's hexes, or a step's draft of them).
 */
export function canTakeIn(
  board: readonly HexState[],
  rules: RunRules,
  tick: number,
  hex: HexState,
  faction: FactionId,
): boolean {
  return (
    !allied(rules, hex.owner, faction) &&
    !hex.razed &&
    hex.walls === 0 &&
    !heldByCityIn(board, rules, tick, hex)
  );
}

/** Whether `faction` would take the hex at `coord` by standing there, as the run stands. */
export function canTake(state: RunState, faction: FactionId, coord: AxialCoord): boolean {
  const hex = findHex(state, coord);
  return hex !== undefined && canTakeIn(state.hexes, state.rules, state.tick, hex, faction);
}

/**
 * The faction takes the hex at `coord` on a step's draft of the board (`takeHex`), the draft
 * copied from the run at the first take (`draft` undefined); the draft back.
 */
export function takeOnDraft(
  draft: MutableHex[] | undefined,
  state: RunState,
  coord: AxialCoord,
  faction: FactionId,
): MutableHex[] {
  const board = draft ?? state.hexes.map((hex) => ({ ...hex }));
  const hex = hexAt(board, coord);
  if (hex) {
    takeHex(board, state.rules, state.tick, hex, faction);
  }
  return board;
}

/**
 * The faction takes the hex. A working city falls with the land it held: every hex of the
 * loser within `cityReach` of it that no other working city of the loser reaches changes
 * hands too (A51). `hexes` is the whole board, mutated in place.
 */
export function takeHex(
  hexes: MutableHex[],
  rules: RunRules,
  tick: number,
  hex: MutableHex,
  faction: FactionId,
): void {
  const loser = hex.owner;
  const fallen = rules.cityHoldsLand && loser !== "none" && isWorkingCity(hex, tick);
  const land = fallen ? landFallingWith(hexes, rules, tick, hex.coord, loser) : [];
  capture(hex, faction);
  for (const held of land) {
    capture(held, faction);
  }
}

/**
 * The land of `loser` that goes with its working city at `city` (A51): within `cityReach` of
 * it, no city itself, and reached by no other working city of the loser.
 */
function landFallingWith<T extends HexState>(
  hexes: readonly T[],
  rules: RunRules,
  tick: number,
  city: AxialCoord,
  loser: FactionId,
): T[] {
  const remaining = hexes.filter(
    (other) => other.owner === loser && isWorkingCity(other, tick) && !hexEquals(other.coord, city),
  );
  return hexes.filter(
    (land) =>
      land.owner === loser &&
      !land.razed &&
      !isWorkingCity(land, tick) &&
      hexDistance(land.coord, city) <= rules.cityReach &&
      !remaining.some((other) => hexDistance(other.coord, land.coord) <= rules.cityReach),
  );
}

/**
 * The land that changes hands with the working city at `coord`, the city's own hex aside
 * (A51); empty for a hex that is no faction's working city and without `cityHoldsLand`. A
 * reading for the screen: `takeHex` hands over the same list.
 */
export function cityLand(state: RunState, coord: AxialCoord): HexState[] {
  const city = findHex(state, coord);
  if (
    !city ||
    city.owner === "none" ||
    !state.rules.cityHoldsLand ||
    !isWorkingCity(city, state.tick)
  ) {
    return [];
  }
  return landFallingWith(state.hexes, state.rules, state.tick, coord, city.owner);
}

/**
 * Step 1a, every tick: a unit that has stood `captureTicks` on a wall-free hex of another
 * owner that no working city of that owner holds takes it (a finished structure changes
 * hands, a build in progress is lost; a working city carries its held land with it, A51).
 * The dwell counts from the step that brought the unit there, so a walker cannot flip land
 * it only passes through unless it `capturesOnArrival`. Takers act in id order on the board
 * as the earlier ones left it. A seat's soldier is paid for the hex it took (C1), and for
 * that hex only when a city's land falls with it; seats are paid in id order.
 */
export function captureByStanding(state: RunState): RunState {
  const { captureTicks } = state.rules;
  const takers = state.units.filter((unit) => state.tick - unit.movedTick >= captureTicks);
  if (takers.length === 0) {
    return state;
  }
  let hexes: MutableHex[] | undefined;
  // C2: the land the squad takes pays the soldier that fell in, while it marches with it; VC: the
  // land its own men take pays the leader on duty, its own hex paid once, as land.
  const ring = state.rules.company?.squadRing ?? 0;
  const ledgers = soldierSeats(state).map((seat) => {
    const leader = enlistedWith(state, seat);
    const led = seatLeader(state, seat);
    return {
      seat,
      squad: leader && marchesWith(state, seat, leader) ? leader.id : undefined,
      men: led && onDuty(state, seat) ? led : undefined,
      took: false,
      squadTook: 0,
    };
  });
  for (const taker of takers) {
    const board = hexes ?? state.hexes;
    const hex = hexAt(board, taker.coord);
    if (!hex || !canTakeIn(board, state.rules, state.tick, hex, taker.owner)) {
      continue;
    }
    hexes = takeOnDraft(hexes, state, taker.coord, taker.owner);
    const takerSeat = isAtWork(state, taker) ? seatOfUnit(state, taker.id) : undefined;
    for (const ledger of ledgers) {
      ledger.took ||= ledger.seat === takerSeat;
      const { squad, men } = ledger;
      if (squad !== undefined && (taker.id === squad || taker.squadOf === squad)) {
        ledger.squadTook += 1;
      } else if (
        men !== undefined &&
        taker.squadOf === men.id &&
        hexDistance(taker.coord, men.coord) <= ring
      ) {
        ledger.squadTook += 1;
      }
    }
  }
  let paid: RunState = hexes ? { ...state, hexes } : state;
  for (const { seat, took, squadTook } of ledgers) {
    paid = payDeeds(took ? payDeed(paid, seat.id, "land") : paid, seat.id, "squad", squadTook);
  }
  return paid;
}
